/**
 * @fileoverview IndexedDB implementation for Signal Store persistence using Dexie.js
 */

import {Injectable, signal} from '@angular/core';
import Dexie from 'dexie';
import {filter, firstValueFrom} from 'rxjs';
import {StoresLoaded$} from './public';

/**
 * Type definition for persisted store data
 * @template D The type of data being stored
 */
export type PersistedStore<D extends object> = {
	storeName: string;
	data: D;
};

export class SignalStorePersistenceDBClass extends Dexie {
	/** Database name constant */
	public static readonly DB_NAME = 'SignalStorePersistenceDB';
	/** Table name constant for storing the persisted data */
	public static readonly STORE_TABLE_NAME = 'stores';
	private static _instance: SignalStorePersistenceDBClass | null = null;
	public stores!: Dexie.Table<PersistedStore<object>, string>;
	public loaded$ = signal(false);
	public readonly allStores$ = signal<Record<string, PersistedStore<object>> | null>(null);
	private initialized: boolean = false;

	protected constructor() {
		super(SignalStorePersistenceDBClass.DB_NAME);
		this.version(1).stores({
			[SignalStorePersistenceDBClass.STORE_TABLE_NAME]: '&storeName',
		});
		this.stores = this.table(SignalStorePersistenceDBClass.STORE_TABLE_NAME);
	}

	public static getInstance() {
		if (!this._instance) {
			this._instance = new SignalStorePersistenceDBClass();
		}
		return this._instance;
	}

	/**
	 * מאתחל את מסד הנתונים, במידה והוא כבר מאותחל ממתין להודעה מ־StoresLoaded$
	 */
	public async init(): Promise<void> {
		if (this.initialized) {
			// ממתין עד שהחנויות נטענו
			await firstValueFrom(StoresLoaded$.pipe(filter(loaded => loaded)));
			return;
		}
		this.initialized = true;
		await this.open();
		this.on('ready', async () => {
			try {
				const stores = await this.stores.toArray();
				const storesMap = stores.reduce(
					(acc, store) => {
						acc[store.storeName] = store;
						return acc;
					},
					{} as Record<string, PersistedStore<object>>,
				);
				this.allStores$.set(storesMap);
				this.loaded$.set(true);
				StoresLoaded$.next(true);
			} catch (error) {
				console.error('שגיאה באתחול מסד הנתונים:', error);
				StoresLoaded$.next(false);
			}
		});
	}
}

/**
 * Service responsible for managing Signal Store persistence in IndexedDB
 * using Dexie.js as the database wrapper.
 */
@Injectable({
	providedIn: 'root',
})
export class SignalStorePersistenceDB {
	/** Database name constant */
	public static readonly DB_NAME = 'SignalStorePersistenceDB';
	/** Table name constant for storing the persisted data */
	public static readonly STORE_TABLE_NAME = 'stores';

	private db = SignalStorePersistenceDBClass.getInstance();

	constructor() {}

	/**
	 * Saves or updates store data in the database
	 * @template D Type of the data being stored
	 * @param storeName Unique identifier for the store
	 * @param data Data to be persisted
	 * @throws Error if save operation fails
	 */
	public async saveStore<D extends object>(storeName: string, data: D): Promise<void> {
		try {
			// שימוש ב-put: מבצע upsert – מעדכן אם רשומה קיימת, או יוצר חדשה
			await SignalStorePersistenceDBClass.getInstance().stores.put({storeName, data});
		} catch (error) {
			console.error(`שגיאה בשמירת store '${storeName}':`, error);
			throw error;
		}
	}

	/**
	 * Checks if a store exists in the database
	 * @param storeName Store identifier to check
	 * @returns Promise resolving to boolean indicating store existence
	 */
	public async hasStore(storeName: string): Promise<boolean> {
		const db = SignalStorePersistenceDBClass.getInstance();
		const record = await db.stores.get(storeName);
		return !!record;
	}

	/**
	 * Loads store data, initializing with default state if not exists
	 * @template D Type of the store data
	 * @param storeName Store identifier
	 * @param initialState Default state to use if store doesn't exist
	 * @returns Promise resolving to the store data
	 */
	public async loadStore<D extends object>(storeName: string, initialState: D): Promise<D> {
		if (!(await this.hasStore(storeName))) {
			await this.saveStore(storeName, initialState);
		}
		return (await this.getStoreState<D>(storeName)) as D;
	}

	/**
	 * Retrieves the current state of a store
	 * @template D Type of the store data
	 * @param storeName Store identifier
	 * @returns Promise resolving to store data or null if not found
	 * @throws Error if retrieval operation fails
	 */
	public async getStoreState<D extends object>(storeName: string): Promise<D | null> {
		const db = SignalStorePersistenceDBClass.getInstance();
		try {
			if (!db.loaded$()) await db.init();
			const record: PersistedStore<object> | null = db.allStores$()?.[storeName] ?? (await db.stores.get(storeName));
			return record ? (record.data as D) : null;
		} catch (error) {
			console.error(`שגיאה בטעינת store '${storeName}':`, error);
			throw error;
		}
	}

	/**
	 * Deletes a specific store from the database
	 * @param storeName Store identifier to delete
	 * @throws Error if deletion operation fails
	 */
	public async deleteStore(storeName: string): Promise<void> {
		const db = SignalStorePersistenceDBClass.getInstance();
		try {
			await db.stores.delete(storeName);
			db.allStores$.update(allStores => {
				if (allStores) {
					delete allStores[storeName];
				}
				return allStores;
			});
		} catch (error) {
			console.error(`שגיאה במחיקת store '${storeName}':`, error);
			throw error;
		}
	}

	/**
	 * Clears all stores from the database
	 * @throws Error if clear operation fails
	 */
	public async clearAllStores(): Promise<void> {
		const db = SignalStorePersistenceDBClass.getInstance();
		try {
			await db.stores.clear();
			db.allStores$.set(null);
		} catch (error) {
			console.error('שגיאה במחיקת כל ה-stores:', error);
			throw error;
		}
	}
}
